// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle (MANDATORY) - Evidence rows for QA reference UI
// 2. Separation of Concerns - Presentation only
// 3. Modular Architecture - Reusable evidence list
//
// Refer to: docs/architecture/modular-design-standards.md
// Refer to: docs/guides/frontend-integration-guide.md
// ============================================================================

/**
 * Qualification / matrix evidence rows: speaker, timing, original text, link to transcript.
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Person,
  AccessTime,
  OpenInNew,
} from '@mui/icons-material';

/** Stable key per evidence row so list keys stay stable if order changes. */
const evidenceRowKey = (ev, index) => {
  const sp = (ev.speaker || 'unknown').toLowerCase();
  const sn = ev.sentenceNumber ?? ev.transcriptIndex ?? '';
  return `${sp}-${sn}-${index}`;
};

const QualificationEvidenceDisplay = ({ evidence, onReferenceTranscription }) => {
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerLabel = (speaker) => {
    const labels = {
      agent: 'Agent',
      customer: 'Customer',
      unknown: 'Unknown',
    };
    return labels[speaker?.toLowerCase()] || 'Unknown';
  };

  const getSpeakerColor = (speaker) => {
    const colors = {
      agent: 'primary',
      customer: 'secondary',
      unknown: 'default',
    };
    return colors[speaker?.toLowerCase()] || 'default';
  };

  const handleScrollToTranscript = (sentenceNumber) => {
    if (onReferenceTranscription && sentenceNumber) {
      onReferenceTranscription(sentenceNumber);
    }
  };

  if (!evidence || !Array.isArray(evidence) || evidence.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
        No evidence available
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Evidence ({evidence.length} item{evidence.length !== 1 ? 's' : ''}):
      </Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {evidence.map((ev, index) => {
          const rowKey = evidenceRowKey(ev, index);
          const originalText = ev.originalText || ev.text || '';
          const speaker = ev.speaker || 'unknown';
          const startTime = ev.originalStartTime || ev.startTime || 0;
          const endTime = ev.originalEndTime || ev.endTime || 0;
          const sentenceNumber = ev.sentenceNumber || ev.transcriptIndex;

          return (
            <Paper
              key={rowKey}
              elevation={1}
              sx={{
                p: 1.5,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                    <Chip
                      icon={<Person />}
                      label={getSpeakerLabel(speaker)}
                      size="small"
                      color={getSpeakerColor(speaker)}
                      variant="outlined"
                    />
                    <Chip
                      icon={<AccessTime />}
                      label={`${formatTime(startTime)} - ${formatTime(endTime)}`}
                      size="small"
                      variant="outlined"
                    />
                    {sentenceNumber && (
                      <Chip
                        label={`Sentence ${sentenceNumber}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                      color: 'text.primary',
                    }}
                  >
                    {originalText || 'No text available'}
                  </Typography>

                  {sentenceNumber && onReferenceTranscription && (
                    <Box sx={{ mt: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleScrollToTranscript(sentenceNumber)}
                        sx={{ p: 0.5 }}
                      >
                        <OpenInNew fontSize="small" />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          Scroll to transcript
                        </Typography>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default QualificationEvidenceDisplay;
